import React, { PropTypes } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import { checkedOutItemShape, borrowingQuotaShape } from '@bibliocommons/bc-prop-types';
import Pagination from '@bibliocommons/pagination';
import ProgressBar from '@bibliocommons/shared-progress-bar';
import { TextButton } from '@bibliocommons/base-buttons';

import BorrowingList from '../../shared/BorrowingList';
import CheckedOutUtilityBar from '../CheckedOutUtilityBar';
import CheckedOutList from '../CheckedOutList';
import CheckedOutBatchActions from '../CheckedOutBatchActions/CheckedOutBatchActions';
import CheckedOutEmptyContentPanel from '../CheckedOutEmptyContentPanel';
import BorrowingHeader from '../../shared/BorrowingHeader';
import RenewFailureOverlayContainer from '../../../../containers/RenewFailureOverlayContainer';

import './CheckedOut.scss';

export class CheckedOut extends BcReactComponent {
  constructor(props) {
    super(props);
    this.state = {
      renewFailureOverlayOpen: false
    };

    this.bindAll(
      'handleSort',
      'handleBatchRenew',
      'handleBatchAddToShelf',
      'handleRenewFailureOverlayOpen',
      'handleRenewFailureOverlayClose'
    );
  }

  handleRenewFailureOverlayOpen() {
    this.setState({ renewFailureOverlayOpen: true });
  }

  handleRenewFailureOverlayClose() {
    this.setState({ renewFailureOverlayOpen: false });
  }

  componentDidMount() {
    this.redirectIfSingleStatus();
  }

  componentDidUpdate(prevProps) {
    const { items, router, selectedStatus, presentStatuses } = this.props;

    // Unselect status if there are no longer any checkouts with status
    if (selectedStatus && items.size === 0) {
      router.replace('/v2/checkedout');
    }

    if (!prevProps.presentStatuses.equals(presentStatuses)) {
      this.redirectIfSingleStatus();
    }
  }

  redirectIfSingleStatus() {
    const { router, presentStatuses, selectedStatus } = this.props;

    // Redirect to the route for a specific status if there is only one status
    if (!selectedStatus && presentStatuses && presentStatuses.size === 1) {
      router.replace(`/v2/checkedout/${presentStatuses.first().toLowerCase()}`);
    }
  }

  getPrintURL() {
    const { search } = this.props.location;
    return `/v2/print/checkedout${search}`;
  }

  allItemsDigital() {
    return this.props.items.every(
      (item) => item.getIn(['bib', 'policy', 'materialType']) === 'DIGITAL'
    );
  }

  renderMoreDetailsLink() {
    return (
      <TextButton handleClick={this.handleRenewFailureOverlayOpen}>
        <FormattedMessage id="more_details" />
      </TextButton>
    );
  }

  handleBatchRenew(items) {
    const { accountId, checkoutActions } = this.props;
    checkoutActions.renew({
      accountId,
      checkoutIds: items.map(item => item.get('id')).toArray(),
      moreDetails: this.renderMoreDetailsLink()
    });
  }

  handleBatchAddToShelf(items, shelfName) {
    const metadataIds = items
      .map(item => item.get('metadataId'))
      .toSet()
      .toList();

    this.props.shelfActions.batchAddToShelf({ metadataIds, shelfName });
  }

  getSortOptions() {
    const currentSort = this.props.router.location.query.sort || 'dueDate';
    return ['dueDate', 'title', 'author', 'format'].reduce(
      (acc, val) =>
        acc.set(
          val,
          Immutable.Map({
            id: val,
            applied: val === currentSort
          })
        ),
      Immutable.Map()
    ).toList();
  }

  handleSort(sortType) {
    const { location, router } = this.props;
    router.push({
      ...location,
      query: {
        ...location.query,
        sort: sortType
      }
    });
  }

  renderBatchActionButtons(itemsForAction) {
    return (
      <CheckedOutBatchActions
        itemsForAction={itemsForAction}
        handleBatchRenew={this.handleBatchRenew}
        handleBatchAddToShelf={this.handleBatchAddToShelf} />
    );
  }

  renderBorrowingList() {
    const { items, showStaffPicksLink, pagination, fetching, ratingsActions } = this.props;
    const actionList = items.map(item => Immutable.Map({
      id: item.get('checkoutId'),
      metadataId: item.get('metadataId'),
      actions: item.get('actions')
    }));

    if (!items || items.size === 0) {
      return (
        <CheckedOutEmptyContentPanel
          fetching={fetching}
          showStaffPicksLink={showStaffPicksLink} />
      );
    }

    return (
      <BorrowingList>
        <CheckedOutUtilityBar
          allItemsDigital={this.allItemsDigital()}
          status={this.props.selectedStatus}
          totalCount={this.props.totalUnfilteredCount}
          materialTypeCounts={this.props.materialTypeCounts}
          sortOptions={this.getSortOptions()}
          printURL={this.getPrintURL()}
          quotas={this.props.quotas}
          pagination={this.props.pagination}
          handleSort={this.handleSort} />
        <CheckedOutList
          accountId={this.props.accountId}
          items={items}
          actionList={actionList}
          shelfTransactions={this.props.shelfTransactions}
          totalCount={this.props.totalCount}
          isFetching={this.props.isFetching}
          workflowActions={this.props.workflowActions}
          checkoutActions={this.props.checkoutActions}
          ratingsActions={ratingsActions}
          checkoutTransactions={this.props.checkoutTransactions}
          ratingsTransactions={this.props.ratingsTransactions}
          handleBatchRenew={this.handleBatchRenew}
          handleBatchAddToShelf={this.handleBatchAddToShelf}
          ratings={this.props.ratings}
          renderBatchActionButtons={(itemsForAction) => this.renderBatchActionButtons(itemsForAction)} />

        <section className="bottom-controls">
          <Pagination
            isFetching={false}
            showPages
            page={pagination.get('page')}
            limit={pagination.get('limit')}
            count={pagination.get('count')}
            pages={pagination.get('pages')} />
        </section>
      </BorrowingList>
    );
  }

  render() {
    return (
      <div className="cp-checked-out">
        <ProgressBar active={this.props.fetching} />
        <div className="title-row">
          <BorrowingHeader
            headingKey="section_title_checkedout"
            taglineKey="section_tagline_checkedout"
            helpLinkPage="005borrowing" />
          {this.renderBorrowingList()}
        </div>

        <RenewFailureOverlayContainer
          open={this.state.renewFailureOverlayOpen}
          handleClose={this.handleRenewFailureOverlayClose} />
      </div>
    );
  }
}

CheckedOut.propTypes = {
  accountId: PropTypes.number,
  isFetching: PropTypes.bool,
  items: ImmutablePropTypes.listOf(checkedOutItemShape),
  materialTypeCounts: ImmutablePropTypes.mapContains({
    PHYSICAL: PropTypes.number,
    DIGITAL: PropTypes.number
  }),
  selectedStatus: PropTypes.string,
  presentStatuses: ImmutablePropTypes.listOf(PropTypes.string),
  totalCount: PropTypes.number,
  totalUnfilteredCount: PropTypes.number,
  showStaffPicksLink: PropTypes.bool,
  quotas: ImmutablePropTypes.listOf(borrowingQuotaShape),
  workflowActions: PropTypes.objectOf(PropTypes.func),
  checkoutActions: PropTypes.objectOf(PropTypes.func),
  shelfActions: PropTypes.objectOf(PropTypes.func),
  ratingsActions: PropTypes.objectOf(PropTypes.func),
  ratings: ImmutablePropTypes.map,
  pagination: ImmutablePropTypes.mapContains({
    page: PropTypes.number,
    limit: PropTypes.number,
    count: PropTypes.number,
    pages: PropTypes.number
  }),
  checkoutTransactions: ImmutablePropTypes.map,
  ratingsTransactions: ImmutablePropTypes.map,
  shelfTransactions: ImmutablePropTypes.map,
  fetching: PropTypes.bool
};

export default withRouter(CheckedOut);



// WEBPACK FOOTER //
// ./app/components/borrowing/checkouts/CheckedOut/CheckedOut.jsx